/**
 * Backend Health Service
 *
 * Checks that the backend APIs can verify Firebase tokens.
 * Called at login to hard-gate the app if the backend is unhealthy.
 */

/**
 * Check backend health by asking /api/health to verify the user's ID token.
 * @param {string} idToken - Firebase ID token
 * @returns {Promise<{ healthy: boolean, error?: string }>}
 */
export async function checkBackendHealth(idToken) {
  // Skip health check if explicitly disabled (local development)
  if (import.meta.env.SKIP_HEALTH_CHECK) {
    console.log('[HealthService] Health check skipped (SKIP_HEALTH_CHECK=true)');
    return { healthy: true };
  }

  try {
    const response = await fetch('/api/health', {
      method: 'GET',
      headers: {
        'X-Firebase-Token': idToken,
      },
    });

    if (!response.ok) {
      return {
        healthy: false,
        error: `Health endpoint returned ${response.status}`,
      };
    }

    const data = await response.json();

    if (data.status === 'degraded') {
      // Log diagnostics to help debug token verification issues
      if (data._diag) {
        console.warn('[HealthService] Server diagnostics:', JSON.stringify(data._diag, null, 2));
      }
      return {
        healthy: false,
        error: data.error || 'Backend services are in a degraded state',
      };
    }

    return { healthy: true };
  } catch (error) {
    return {
      healthy: false,
      error: error.message || 'Cannot reach backend services',
    };
  }
}
